import * as THREE from 'three';

/** 1 scene unit = 1 cm (see ModelScaler). */
/** Tick / base grid step = 2 mm */
const TICK_STEP_CM = 2.0;
/** Axis span = model extent × this (e.g. 10% margin). */
const AXIS_LENGTH_FACTOR = 1.1;
const MIN_AXIS_CM = 1e-4;
/** XZ floor grid half-span (cm): grid fills [-R,R]×[-R,R] on xz at y≈0; not tied to axis %. XY grid unchanged. */
const XZ_GRID_HALF_EXTENT_CM = 10000;

const LEN_MINOR = 0.18;
const LEN_MAJOR = 0.55;

/** Matches UI readout (Tailwind red-600); glyph fill is redrawn — material.color cannot brighten dark text. */
const LABEL_HIGHLIGHT_FILL = '#dc2626';

const GRID_Z = -0.002;
/** XZ floor grids sit slightly below y=0 so axis lines read cleanly */
const GRID_Y_PLANE = -0.002;
const GRID_GRAY = 0x94a3b8;
const GRID_OPACITY = 0.28;

/** XY plane z≈GRID_Z: grid only between +X and +Y → [0,lenX]×[0,lenY]. */
function buildSimpleGrayBaseGrid(lenX, lenY) {
  const verts = [];
  for (let x = TICK_STEP_CM; x < lenX + 1e-9; x += TICK_STEP_CM) {
    verts.push(x, 0, GRID_Z, x, lenY, GRID_Z);
  }
  for (let y = TICK_STEP_CM; y < lenY + 1e-9; y += TICK_STEP_CM) {
    verts.push(0, y, GRID_Z, lenX, y, GRID_Z);
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  return geom;
}

/**
 * @param {number[]} verts
 * @param {number} gy  world y of XZ plane
 */
function addXZRectGrid(verts, xMin, xMax, zMin, zMax, gy) {
  for (let x = xMin + TICK_STEP_CM; x < xMax - 1e-9; x += TICK_STEP_CM) {
    verts.push(x, gy, zMin, x, gy, zMax);
  }
  for (let z = zMin + TICK_STEP_CM; z < zMax - 1e-9; z += TICK_STEP_CM) {
    verts.push(xMin, gy, z, xMax, gy, z);
  }
}

/** XZ plane (y≈0): full [-R,R]×[-R,R] grid (covers all four quadrants from corner origin); R ≫ axis %. */
function buildXZGridsBetweenXMZ(lenX, lenZ) {
  const verts = [];
  const gy = GRID_Y_PLANE;
  const R = Math.max(XZ_GRID_HALF_EXTENT_CM, lenX, lenZ);
  addXZRectGrid(verts, -R, R, -R, R, gy);
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  return geom;
}

function mergeLineGeometries(...geoms) {
  let n = 0;
  for (const g of geoms) {
    if (g.attributes.position) n += g.attributes.position.array.length;
  }
  const arr = new Float32Array(n);
  let off = 0;
  for (const g of geoms) {
    const p = g.attributes.position.array;
    arr.set(p, off);
    off += p.length;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(arr, 3));
  return out;
}

/** How many numbered grid steps fit on [0, maxLen] (same rule as grid lines). */
function countTickLabelsAlongAxis(maxLen) {
  if (maxLen < TICK_STEP_CM - 1e-9) return 0;
  return Math.floor((maxLen + 1e-9) / TICK_STEP_CM);
}

/** Text on scale sprites at distance `t` (scene units = cm). */
function formatScaleLabelValue(t) {
  const r = Math.round(t * 1e6) / 1e6;
  if (Math.abs(r - Math.round(r)) < 1e-5) return String(Math.round(r));
  return String(r);
}

/** i = tick index along axis (each tick = TICK_STEP_CM); longer tick every 1 cm (every 5 steps). */
function tickDepthForStep(i) {
  if (i % 5 === 0) return LEN_MAJOR;
  return LEN_MINOR;
}

function configureLabelTexture(tex) {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 1;
}

/** Rasterize label text; fillStyleCss is CSS color string. */
function renderLabelCanvas(text, fillStyleCss, fontSizePx) {
  const pad = 12;
  const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2.5) : 2;

  const measureCvs = document.createElement('canvas');
  const mctx = measureCvs.getContext('2d');
  mctx.font = `400 ${fontSizePx}px Inter, system-ui, sans-serif`;
  const metrics = mctx.measureText(text);
  const wLogical = Math.ceil(metrics.width) + pad * 2;
  const hLogical = fontSizePx + pad * 2;

  const cvs = document.createElement('canvas');
  cvs.width = Math.max(1, Math.floor(wLogical * dpr));
  cvs.height = Math.max(1, Math.floor(hLogical * dpr));

  const ctx2 = cvs.getContext('2d');
  ctx2.scale(dpr, dpr);
  ctx2.font = `400 ${fontSizePx}px Inter, system-ui, sans-serif`;
  ctx2.strokeStyle = '#262626';
  ctx2.lineWidth = Math.max(4, Math.round(fontSizePx * 0.08));
  ctx2.textBaseline = 'middle';
  ctx2.textAlign = 'center';
  ctx2.strokeText(text, wLogical / 2, hLogical / 2);
  ctx2.fillStyle = fillStyleCss;
  ctx2.fillText(text, wLogical / 2, hLogical / 2);

  return { cvs, wLogical, hLogical };
}

function makeLabelSprite(text, colorHex, fontSizePx, labelScreenSize) {
  const defaultFill = new THREE.Color(colorHex).getStyle();
  const { cvs, wLogical, hLogical } = renderLabelCanvas(text, defaultFill, fontSizePx);

  const tex = new THREE.CanvasTexture(cvs);
  configureLabelTexture(tex);

  const mat = new THREE.SpriteMaterial({
    map: tex,
    depthTest: true,
    depthWrite: false,
    sizeAttenuation: false,
  });
  const spr = new THREE.Sprite(mat);
  const aspect = wLogical / hLogical;
  spr.scale.set(labelScreenSize * aspect, labelScreenSize, 1);
  spr.renderOrder = 1001;
  spr.userData.labelText = text;
  spr.userData.fontSizePx = fontSizePx;
  spr.userData.labelScreenSize = labelScreenSize;
  spr.userData.defaultFillStyle = defaultFill;
  return spr;
}

function setSpriteLabelHighlightVisual(spr, highlighted) {
  if (!spr?.material) return;
  const fill = highlighted ? LABEL_HIGHLIGHT_FILL : spr.userData.defaultFillStyle;
  const { labelText, fontSizePx, labelScreenSize } = spr.userData;
  const oldMap = spr.material.map;
  const { cvs, wLogical, hLogical } = renderLabelCanvas(labelText, fill, fontSizePx);
  const tex = new THREE.CanvasTexture(cvs);
  configureLabelTexture(tex);
  spr.material.map = tex;
  spr.material.color.set(0xffffff);
  if (oldMap) oldMap.dispose();
  const aspect = wLogical / hLogical;
  spr.scale.set(labelScreenSize * aspect, labelScreenSize, 1);
}

function disposeSubtree(root) {
  root.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    const m = child.material;
    if (m) {
      if (m.map) m.map.dispose();
      m.dispose();
    }
  });
}

function lengthsNeedRebuild(a, b, c, a0, b0, c0, eps = 1e-3) {
  return Math.abs(a - a0) > eps || Math.abs(b - b0) > eps || Math.abs(c - c0) > eps;
}

/**
 * Corner metric scale: +X, +Y, +Z from origin; XY grid only in +X/+Y quadrant; XZ grid large square on xz (extends past axis %); ruler ticks + numeric labels on TICK_STEP_CM.
 * Axis length each = model extent on that axis × 1.1 (min tiny epsilon).
 * @param {{ position?: THREE.Vector3, lineColor?: number }} options
 */
export function createMetricLScaleOverlay({
  position = new THREE.Vector3(0, 0, 0),
  lineColor = 0x1e293b,
} = {}) {
  const group = new THREE.Group();
  group.name = 'MetricLScaleOverlay';
  group.position.copy(position);

  const contentRoot = new THREE.Group();
  contentRoot.name = 'MetricScaleAxesContent';
  group.add(contentRoot);

  const state = {
    lenX: 0,
    lenY: 0,
    lenZ: 0,
    labels: /** @type {THREE.Sprite[]} */ ([]),
    /** X+/Y+/Z+ and −X/−Y/−Z — not part of cm highlight indices */
    axisTipSprites: /** @type {THREE.Sprite[]} */ ([]),
    numXLabels: 0,
    numYLabels: 0,
    numZLabels: 0,
  };

  const _tmpN = new THREE.Vector3();
  const _bbox = new THREE.Box3();
  const _size = new THREE.Vector3();

  let lastHighlightIndices = new Set();
  let lastHighlightKey = null;

  function clearLabelHighlight() {
    for (const idx of lastHighlightIndices) {
      const spr = state.labels[idx];
      if (spr) setSpriteLabelHighlightVisual(spr, false);
    }
    lastHighlightIndices.clear();
    lastHighlightKey = null;
  }

  function setLabelHighlight(xCm, yCm, zCm) {
    const maxTickX = countTickLabelsAlongAxis(state.lenX);
    const maxTickY = countTickLabelsAlongAxis(state.lenY);
    const maxTickZ = countTickLabelsAlongAxis(state.lenZ);
    const xi = THREE.MathUtils.clamp(Math.round(xCm / TICK_STEP_CM), 0, Math.max(0, maxTickX));
    const yi = THREE.MathUtils.clamp(Math.round(yCm / TICK_STEP_CM), 0, Math.max(0, maxTickY));
    const zi = THREE.MathUtils.clamp(Math.round(zCm / TICK_STEP_CM), 0, Math.max(0, maxTickZ));
    const indices = new Set();
    indices.add(xi === 0 ? 0 : xi);
    indices.add(yi === 0 ? 0 : state.numXLabels + yi);
    indices.add(zi === 0 ? 0 : state.numXLabels + state.numYLabels + zi);
    const key = [...indices].sort((a, b) => a - b).join(',');
    if (key === lastHighlightKey) return;

    clearLabelHighlight();
    lastHighlightKey = key;
    for (const idx of indices) {
      const spr = state.labels[idx];
      if (spr) {
        setSpriteLabelHighlightVisual(spr, true);
        lastHighlightIndices.add(idx);
      }
    }
  }

  function rebuildAxes(lenX, lenY, lenZ) {
    clearLabelHighlight();
    while (contentRoot.children.length > 0) {
      const ch = contentRoot.children[0];
      contentRoot.remove(ch);
      disposeSubtree(ch);
    }

    const gridGeom = mergeLineGeometries(
      buildSimpleGrayBaseGrid(lenX, lenY),
      buildXZGridsBetweenXMZ(lenX, lenZ)
    );
    const gridMat = new THREE.LineBasicMaterial({
      color: GRID_GRAY,
      transparent: true,
      opacity: GRID_OPACITY,
      depthTest: true,
      depthWrite: false,
    });
    const gridLines = new THREE.LineSegments(gridGeom, gridMat);
    gridLines.renderOrder = 997;
    contentRoot.add(gridLines);

    const baselinePositions = new Float32Array([
      0,
      0,
      0,
      lenX,
      0,
      0,
      0,
      0,
      0,
      0,
      lenY,
      0,
      0,
      0,
      0,
      0,
      0,
      lenZ,
    ]);
    const baselineGeom = new THREE.BufferGeometry();
    baselineGeom.setAttribute('position', new THREE.BufferAttribute(baselinePositions, 3));
    const baselineMat = new THREE.LineBasicMaterial({
      color: lineColor,
      depthTest: true,
      depthWrite: false,
    });
    const baselines = new THREE.LineSegments(baselineGeom, baselineMat);
    baselines.renderOrder = 1000;
    contentRoot.add(baselines);

    const tickVerts = [];
    const pushTicksAlong = (axis, maxLen) => {
      const n = Math.min(500000, Math.ceil(maxLen / TICK_STEP_CM + 1e-9));
      for (let i = 1; i <= n; i++) {
        const t = i * TICK_STEP_CM;
        if (t > maxLen + 1e-9) break;
        const d = tickDepthForStep(i);
        if (axis === 'x') tickVerts.push(t, 0, 0, t, -d, 0);
        else if (axis === 'y') tickVerts.push(0, t, 0, -d, t, 0);
        else tickVerts.push(0, 0, t, 0, -d, t);
      }
    };
    pushTicksAlong('x', lenX);
    pushTicksAlong('y', lenY);
    pushTicksAlong('z', lenZ);

    const tickGeom = new THREE.BufferGeometry();
    tickGeom.setAttribute('position', new THREE.Float32BufferAttribute(tickVerts, 3));
    const tickMat = new THREE.LineBasicMaterial({
      color: lineColor,
      opacity: 0.9,
      transparent: true,
      depthTest: true,
      depthWrite: false,
    });
    const ticks = new THREE.LineSegments(tickGeom, tickMat);
    ticks.renderOrder = 1000;
    contentRoot.add(ticks);

    const labels = [];
    const fontSizePx = 128;
    const labelScreenSize = 0.68;
    const labelOffset = LEN_MAJOR + labelScreenSize * 0.55 + 0.35;

    const corner = makeLabelSprite('0', lineColor, fontSizePx, labelScreenSize);
    corner.position.set(-labelOffset * 0.5, -labelOffset * 0.5, 0);
    corner.userData.basePos = corner.position.clone();
    corner.userData.scaleLabel = { axis: 'corner', cm: 0 };
    contentRoot.add(corner);
    labels.push(corner);

    const numXLabels = countTickLabelsAlongAxis(lenX);
    const numYLabels = countTickLabelsAlongAxis(lenY);
    const numZLabels = countTickLabelsAlongAxis(lenZ);

    for (let i = 1; i <= numXLabels; i++) {
      const t = i * TICK_STEP_CM;
      const s = makeLabelSprite(formatScaleLabelValue(t), lineColor, fontSizePx, labelScreenSize);
      s.position.set(t, -labelOffset, 0);
      s.userData.basePos = s.position.clone();
      s.userData.scaleLabel = { axis: 'x', cm: i };
      contentRoot.add(s);
      labels.push(s);
    }

    for (let i = 1; i <= numYLabels; i++) {
      const t = i * TICK_STEP_CM;
      const s = makeLabelSprite(formatScaleLabelValue(t), lineColor, fontSizePx, labelScreenSize);
      s.position.set(-labelOffset, t, 0);
      s.userData.basePos = s.position.clone();
      s.userData.scaleLabel = { axis: 'y', cm: i };
      contentRoot.add(s);
      labels.push(s);
    }

    for (let i = 1; i <= numZLabels; i++) {
      const t = i * TICK_STEP_CM;
      const s = makeLabelSprite(formatScaleLabelValue(t), lineColor, fontSizePx, labelScreenSize);
      s.position.set(-labelOffset, 0, t);
      s.userData.basePos = s.position.clone();
      s.userData.scaleLabel = { axis: 'z', cm: i };
      contentRoot.add(s);
      labels.push(s);
    }

    const tipFontPx = 100;
    const tipScreenSize = 0.56;
    const tipAlong = 0.38;
    const tipNeg = Math.min(0.55, Math.min(lenX, lenY, lenZ) * 0.08 + 0.22);

    const axisTips = [
      { text: 'X+', pos: [lenX + tipAlong, -labelOffset * 0.62, 0] },
      { text: 'Y+', pos: [-labelOffset * 0.62, lenY + tipAlong, 0] },
      { text: 'Z+', pos: [-labelOffset * 0.62, 0, lenZ + tipAlong] },
      { text: '−X', pos: [-tipNeg, -labelOffset * 0.38, 0] },
      { text: '−Y', pos: [-labelOffset * 0.38, -tipNeg, 0] },
      { text: '−Z', pos: [-labelOffset * 0.38, 0, -tipNeg] },
    ];
    const tipSprites = [];
    for (const { text, pos } of axisTips) {
      const sp = makeLabelSprite(text, lineColor, tipFontPx, tipScreenSize);
      sp.position.set(pos[0], pos[1], pos[2]);
      sp.userData.basePos = sp.position.clone();
      sp.userData.axisTip = true;
      contentRoot.add(sp);
      tipSprites.push(sp);
    }
    state.axisTipSprites = tipSprites;

    state.lenX = lenX;
    state.lenY = lenY;
    state.lenZ = lenZ;
    state.labels = labels;
    state.numXLabels = numXLabels;
    state.numYLabels = numYLabels;
    state.numZLabels = numZLabels;
  }

  return {
    group,
    clearLabelHighlight,
    setLabelHighlight,
    syncToMesh(mesh) {
      if (!mesh) return;
      _bbox.setFromObject(mesh);
      if (_bbox.isEmpty()) return;
      _bbox.getSize(_size);
      const lenX = Math.max(MIN_AXIS_CM, _size.x * AXIS_LENGTH_FACTOR);
      const lenY = Math.max(MIN_AXIS_CM, _size.y * AXIS_LENGTH_FACTOR);
      const lenZ = Math.max(MIN_AXIS_CM, _size.z * AXIS_LENGTH_FACTOR);
      group.position.set(_bbox.min.x, _bbox.min.y, _bbox.min.z);
      if (state.labels.length === 0 || lengthsNeedRebuild(lenX, lenY, lenZ, state.lenX, state.lenY, state.lenZ)) {
        rebuildAxes(lenX, lenY, lenZ);
      }
    },
    update(camera) {
      if (!camera) return;
      const billboards = state.labels.concat(state.axisTipSprites);
      for (const s of billboards) {
        if (!s.userData.basePos) continue;
        s.position.copy(s.userData.basePos);
        s.position.addScaledVector(_tmpN.setFromMatrixColumn(camera.matrixWorld, 2), 0.002);
      }
    },
    dispose() {
      disposeSubtree(group);
    },
  };
}
